import { useEffect, useState, useMemo } from "react";
import { api, formatINR } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Filter, Download } from "lucide-react";
import { toast } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  type: "expense",
  amount: "",
  category: "",
  note: "",
  date: todayStr(),
});

export default function Transactions() {
  const [txns, setTxns] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = async () => {
    try {
      const [t, c] = await Promise.all([api.get("/transactions"), api.get("/categories")]);
      setTxns(t.data);
      setCats(c.data);
    } catch (e) {
      console.warn("Transactions load failed", e?.response?.status);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredCats = useMemo(
    () => cats.filter((c) => c.type === form.type),
    [cats, form.type]
  );

  const visible = useMemo(() => {
    return txns.filter((t) => {
      if (filterType !== "all" && t.type !== filterType) return false;
      if (filterCategory !== "all" && t.category !== filterCategory) return false;
      if (startDate && t.date < startDate) return false;
      if (endDate && t.date > endDate) return false;
      return true;
    });
  }, [txns, filterType, filterCategory, startDate, endDate]);

  const clearFilters = () => {
    setFilterType("all");
    setFilterCategory("all");
    setStartDate("");
    setEndDate("");
  };

  const exportCSV = () => {
    if (visible.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const header = ["Date", "Type", "Category", "Amount (INR)", "Note"];
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = visible.map((t) =>
      [t.date, t.type, t.category, t.amount, t.note || ""].map(esc).join(",")
    );
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `kosh-transactions-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${visible.length} transactions`);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      type: t.type,
      amount: String(t.amount),
      category: t.category,
      note: t.note || "",
      date: t.date,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!form.category) {
      toast.error("Pick a category");
      return;
    }
    const payload = {
      type: form.type,
      amount: Number(form.amount),
      category: form.category,
      note: form.note,
      date: form.date,
    };
    try {
      if (editingId) {
        await api.put(`/transactions/${editingId}`, payload);
        toast.success("Transaction updated");
      } else {
        await api.post("/transactions", payload);
        toast.success("Transaction saved");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="transactions-page">
      <SectionHeader
        overline="Ledger"
        title="Transactions"
        description="Every credit and debit, kept honest."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                data-testid="open-add-transaction"
                className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white rounded-lg px-5 py-2.5 h-auto"
              >
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Add
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white" data-testid="transaction-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight font-medium">
                  {editingId ? "Edit transaction" : "New transaction"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {["expense", "income"].map((tp) => (
                    <button
                      key={tp}
                      type="button"
                      data-testid={`type-${tp}`}
                      onClick={() =>
                        setForm({ ...form, type: tp, category: "" })
                      }
                      className={`py-2.5 rounded-lg text-sm capitalize border transition-colors ${
                        form.type === tp
                          ? tp === "income"
                            ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
                            : "bg-[#D26A59] text-white border-[#D26A59]"
                          : "bg-white text-[#212421] border-[#EAE8E3] hover:bg-[#F3F1ED]"
                      }`}
                    >
                      {tp}
                    </button>
                  ))}
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Amount (₹)</Label>
                  <Input
                    data-testid="input-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="mt-1.5 font-mono-num text-lg"
                    placeholder="0.00"
                    autoFocus
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Category</Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger data-testid="select-category" className="mt-1.5">
                      <SelectValue placeholder="Choose a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCats.map((c) => (
                        <SelectItem key={c.id} value={c.name}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Date</Label>
                  <Input
                    data-testid="input-date"
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Note (optional)</Label>
                  <Textarea
                    data-testid="input-note"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    rows={2}
                    placeholder="What was this for?"
                    className="mt-1.5"
                  />
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setOpen(false)}
                    data-testid="cancel-transaction"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="save-transaction"
                    className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
                  >
                    {editingId ? "Save changes" : "Save"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-[#EAE8E3] rounded-lg overflow-hidden">
        <div className="flex items-center gap-3 p-4 border-b border-[#EAE8E3] flex-wrap">
          <Filter className="w-4 h-4 text-[#696E69]" strokeWidth={1.5} />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-32 h-9" data-testid="filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="income">Income</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-48 h-9" data-testid="filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => (
                <SelectItem key={c.id} value={c.name}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5">
            <Input
              data-testid="filter-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-9 w-40"
              aria-label="Start date"
            />
            <span className="text-[#696E69] text-xs">→</span>
            <Input
              data-testid="filter-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-9 w-40"
              aria-label="End date"
            />
          </div>
          {(filterType !== "all" || filterCategory !== "all" || startDate || endDate) && (
            <button
              onClick={clearFilters}
              data-testid="clear-filters"
              className="text-xs text-[#696E69] hover:text-[#212421] underline underline-offset-2"
            >
              Clear
            </button>
          )}
          <span className="ml-auto text-xs text-[#696E69] font-mono-num">
            {visible.length} entries
          </span>
          <button
            onClick={exportCSV}
            data-testid="export-csv"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#EAE8E3] text-xs text-[#212421] hover:bg-[#F3F1ED] transition-colors"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Export CSV
          </button>
        </div>

        <table className="w-full">
          <thead>
            <tr>
              <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Date</th>
              <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Category</th>
              <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Note</th>
              <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-right px-6 py-4">Amount</th>
              <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-right px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="transactions-table-body">
            {visible.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center text-sm text-[#696E69] py-16">
                  No transactions match these filters.
                </td>
              </tr>
            )}
            {visible.map((t) => (
              <tr
                key={t.id}
                className="border-t border-[#EAE8E3] hover:bg-[#FBFBF9] transition-colors"
                data-testid={`txn-row-${t.id}`}
              >
                <td className="px-6 py-4 text-sm font-mono-num text-[#696E69]">{t.date}</td>
                <td className="px-6 py-4 text-sm">
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${
                      t.type === "income" ? "bg-[#5C8065]" : "bg-[#D26A59]"
                    }`}
                  />
                  {t.category}
                </td>
                <td className="px-6 py-4 text-sm text-[#696E69] max-w-xs truncate">
                  {t.note || "—"}
                </td>
                <td
                  className={`px-6 py-4 text-sm font-mono-num text-right ${
                    t.type === "income" ? "text-[#1F4E3D]" : "text-[#D26A59]"
                  }`}
                >
                  {t.type === "income" ? "+" : "−"} ₹{formatINR(t.amount)}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => openEdit(t)}
                      data-testid={`edit-${t.id}`}
                      className="p-2 rounded-md hover:bg-[#F3F1ED] text-[#696E69]"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => remove(t.id)}
                      data-testid={`delete-${t.id}`}
                      className="p-2 rounded-md hover:bg-[#D26A59]/10 text-[#D26A59]"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
